import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const SETTINGS_META = [
  { key: 'fuel_surcharge',          label: 'Fuel Surcharge',               suffix: '%',  multiplier: 100,  description: 'Applied to base charge on every shipment' },
  { key: 'dim_factor',              label: 'Dim Factor',                   suffix: '',   multiplier: 1,    description: 'Divisor in dimensional weight formula (L×W×H÷factor)' },
  { key: 'liftgate_weight',         label: 'Liftgate Auto Threshold',      suffix: 'lbs', multiplier: 1,   description: 'Liftgate triggers automatically when piece weight ≥ this value' },
  { key: 'twoman_weight',           label: 'Two-Man Weight Threshold',     suffix: 'lbs', multiplier: 1,   description: 'Two-man triggers automatically when weight exceeds this value' },
  { key: 'twoman_weight_secondary', label: 'Two-Man Secondary Threshold',  suffix: 'lbs', multiplier: 1,   description: 'Two-man also triggers when weight > this AND any dimension > the dim threshold' },
  { key: 'twoman_dim',              label: 'Two-Man Dimension Threshold',  suffix: 'in',  multiplier: 1,   description: 'Dimension threshold used with the secondary weight check' },
];

const EMPTY_EMAIL = { invoice_email_to: '', invoice_email_cc: '', invoice_email_body: '' };

const CustomerSettings = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [original, setOriginal] = useState({});
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const [email, setEmail] = useState(EMPTY_EMAIL);
  const [emailOriginal, setEmailOriginal] = useState(EMPTY_EMAIL);
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    fetchData();
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [settingsRes, custRes] = await Promise.all([
        API.get(`/customers/${customerId}/settings`),
        API.get('/customers')
      ]);
      const s = settingsRes.data;
      const display = { ...s, fuel_surcharge: parseFloat((s.fuel_surcharge * 100).toFixed(4)) };
      setSettings(display);
      setOriginal(display);
      const cust = custRes.data.find(c => c.customer_id === parseInt(customerId));
      if (cust) setCustomerName(cust.name);

      // Invoice email config (boss/admin only — tolerate failure)
      try {
        const ecRes = await API.get(`/invoices/email-config/${customerId}`);
        const cfg = { ...EMPTY_EMAIL, ...ecRes.data };
        setEmail(cfg);
        setEmailOriginal(cfg);
      } catch {
        // not permitted or none set — leave empty
      }
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key) => {
    setSaving(key);
    try {
      let value = parseFloat(settings[key]);
      if (key === 'fuel_surcharge') value = value / 100;
      await API.put(`/customers/${customerId}/settings/${key}`, { value });
      setOriginal(prev => ({ ...prev, [key]: settings[key] }));
      toast.success('Setting saved');
    } catch {
      toast.error('Failed to save setting');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveEmail = async () => {
    setSavingEmail(true);
    try {
      const res = await API.put(`/invoices/email-config/${customerId}`, email);
      const cfg = { ...EMPTY_EMAIL, ...res.data };
      setEmail(cfg);
      setEmailOriginal(cfg);
      toast.success('Invoice email settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save email settings');
    } finally {
      setSavingEmail(false);
    }
  };

  const hasChanged = (key) => settings[key] !== original[key];
  const emailChanged = JSON.stringify(email) !== JSON.stringify(emailOriginal);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;

  const fieldLabel = { display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' };
  const fieldInput = { width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };

  return (
    <div className="fcc-fade-in" style={{ padding: '24px', maxWidth: '700px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600' }}>Settings</h1>
          <p style={{ color: '#5B6B7F', fontSize: '14px' }}>Customer: <strong>{customerName}</strong></p>
        </div>
        <button onClick={() => navigate('/dashboard')} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
          ← Dashboard
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {SETTINGS_META.map(meta => (
          <div key={meta.key} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '16px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: '600', fontSize: '14px' }}>{meta.label}</p>
                <p style={{ color: '#5B6B7F', fontSize: '12px', marginTop: '2px' }}>{meta.description}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="number"
                    step="any"
                    value={settings[meta.key] ?? ''}
                    onChange={e => setSettings(prev => ({ ...prev, [meta.key]: e.target.value }))}
                    style={{
                      padding: '8px 10px', border: `1px solid ${hasChanged(meta.key) ? '#f59e0b' : '#E2E8F0'}`,
                      borderRadius: '6px', fontSize: '14px', outline: 'none', width: '100px',
                      background: hasChanged(meta.key) ? '#FFF8EC' : 'white'
                    }}
                  />
                  {meta.suffix && <span style={{ fontSize: '13px', color: '#5B6B7F', minWidth: '24px' }}>{meta.suffix}</span>}
                </div>
                <button
                  onClick={() => handleSave(meta.key)}
                  disabled={!hasChanged(meta.key) || saving === meta.key}
                  className="fcc-press"
                  style={{
                    background: hasChanged(meta.key) ? '#F5A800' : '#E2E8F0',
                    color: hasChanged(meta.key) ? 'white' : '#8595A6',
                    border: 'none', borderRadius: '6px', padding: '8px 16px',
                    cursor: hasChanged(meta.key) ? 'pointer' : 'not-allowed',
                    fontSize: '13px', fontWeight: '500'
                  }}
                >
                  {saving === meta.key ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Invoice email settings */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '20px', marginTop: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: '600' }}>Invoice Email</h2>
          <p style={{ color: '#5B6B7F', fontSize: '12px', marginTop: '2px' }}>
            Where invoices for this customer are sent when a boss clicks “Email to Customer.”
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={fieldLabel}>Send To <span style={{ color: '#8595A6', fontWeight: '400' }}>(comma-separated)</span></label>
            <input
              style={fieldInput}
              placeholder="billing@customer.com, ap@customer.com"
              value={email.invoice_email_to}
              onChange={e => setEmail(prev => ({ ...prev, invoice_email_to: e.target.value }))}
            />
          </div>
          <div>
            <label style={fieldLabel}>CC <span style={{ color: '#8595A6', fontWeight: '400' }}>(optional, comma-separated)</span></label>
            <input
              style={fieldInput}
              placeholder="manager@customer.com"
              value={email.invoice_email_cc}
              onChange={e => setEmail(prev => ({ ...prev, invoice_email_cc: e.target.value }))}
            />
          </div>
          <div>
            <label style={fieldLabel}>Message Body</label>
            <textarea
              style={{ ...fieldInput, minHeight: '120px', resize: 'vertical', fontFamily: 'inherit' }}
              placeholder={'Please find attached invoice {{invoice_number}}.\n\nAmount due: {{amount_due}}\n\nThank you,\nFlorida Cargo Carrier'}
              value={email.invoice_email_body}
              onChange={e => setEmail(prev => ({ ...prev, invoice_email_body: e.target.value }))}
            />
            <p style={{ fontSize: '12px', color: '#8595A6', marginTop: '6px' }}>
              Placeholders: <code>{'{{invoice_number}}'}</code>, <code>{'{{amount_due}}'}</code>, <code>{'{{bill_to}}'}</code>. Leave blank to use a default message. The invoice PDF is always attached; the subject is always <strong>FCC &lt;invoice #&gt;</strong>.
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleSaveEmail}
              disabled={!emailChanged || savingEmail}
              className="fcc-press"
              style={{
                background: emailChanged ? '#F5A800' : '#E2E8F0',
                color: emailChanged ? 'white' : '#8595A6',
                border: 'none', borderRadius: '6px', padding: '9px 20px',
                cursor: emailChanged ? 'pointer' : 'not-allowed',
                fontSize: '14px', fontWeight: '500'
              }}
            >
              {savingEmail ? 'Saving...' : 'Save Email Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSettings;
